import { ApolloCache } from '@apollo/client'
import Bugsnag from '@bugsnag/js'
import { normalizeLineItem } from 'app/lib/apollo/normalizers/LineItem'
import { MutationOptions, useDestroy } from 'app/lib/hooks/useMutation'
import { useQuery } from 'app/lib/hooks/useQuery'
import { queries, RecordTypeEnum } from 'app/queries'
import * as rentalsCheckout from 'app/queries/Rentals/Checkout'

const readRental = (cache: ApolloCache<unknown>, rentalId: string) => {
  return cache.readQuery<{ rental: IRental }>({
    query: rentalsCheckout.RENTAL_CHECKOUT_PRODUCTS_QUERY,
    variables: { id: rentalId, test: true }
  })?.rental
}

const writeRental = (cache: ApolloCache<unknown>, newRental: IRental) => {
  return cache.writeQuery({
    query: rentalsCheckout.RENTAL_CHECKOUT_PRODUCTS_QUERY,
    variables: { id: newRental.id },
    data: { rental: newRental }
  })
}

export function useProductIndex({ id }: { id: string }) {
  const { data, loading, refetch } = useQuery<{ rental: IRental }>(
    rentalsCheckout.RENTAL_CHECKOUT_PRODUCTS_QUERY,
    {
      variables: { id },
      normalizer: ({ rental }) => ({
        rental: {
          ...rental,
          products: (rental.products || []).map(normalizeLineItem)
        }
      })
    }
  )

  return { data: data?.rental?.products || [], loading, refetch }
}

export function useProductDelete() {
  const { loading, execute: executeDestroy } = useDestroy(queries.lineItem.delete, {
    recordType: RecordTypeEnum.lineItem,
    silentSuccess: true
  })

  return { execute: executeDestroy, loading }
}

export function useSubItemDelete() {
  const { loading, execute: executeDestroy } = useDestroy(queries.subItem.delete, {
    recordType: RecordTypeEnum.subItem
  })

  return { execute: executeDestroy, loading }
}

export function useProductCache(rentalId: string) {
  const execute: MutationOptions['update'] = (cache, { data }) => {
    try {
      const { lineItem, errors }: { lineItem: ILineItem; errors: unknown[] } =
        data.createCustomerLineItem

      if (errors?.length) {
        throw new Error(errors[0] as string)
      }

      if (lineItem?.id) {
        const cachedRental = readRental(cache, rentalId)

        if (!cachedRental) return

        const newRental = {
          ...cachedRental,
          products: [...cachedRental.products, lineItem]
        }

        writeRental(cache, newRental)
      }
    } catch (error) {
      Bugsnag.notify(`Error in useProductCache: ${JSON.stringify(error)}`)
    }
  }

  return execute
}
